import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import KycVerification from '../components/KycVerification';
import './pages.css';
import './KycPage.css';

function KycPage() {
    const navigate = useNavigate();
    const [user, setUser] = useState({ id: '', name: '', kycStatus: '', panNumber: '', phone: '', email: '', aadhaar: null });
    const [isLoading, setIsLoading] = useState(true);
    const API_URL = process.env.REACT_APP_API_URL || '';

    const fetchProfile = useCallback(() => {
        const session = JSON.parse(localStorage.getItem('userSession'));
        if (!session?.userId) return setIsLoading(false);
        setIsLoading(true);
        fetch(`${API_URL}/api/auth/profile/${session.userId}`, {
            headers: {
                'Authorization': `Bearer ${session.token}`
            }
        })
            .then(r => r.json())
            .then(data => {
                if (data.success) {
                    const pennyDrop = data.kycRecords?.pennyDrop || {};
                    const salaryProfile = data.kycRecords?.salaryProfile || {};
                    const aadhaar = data.kycRecords?.aadhaar || {};
                    setUser({
                        id: data.user.id,
                        name: data.user.name || aadhaar.name || '',
                        kycStatus: data.user.kycStatus,
                        panNumber: data.user.panNumber || data.user.pan_number || '',
                        phone: data.user.phone || '',
                        email: data.user.email || '',
                        bankAccountNumber: pennyDrop.accountNumber || '',
                        bankIfscCode: pennyDrop.ifscCode || '',
                        bankBeneficiaryName: pennyDrop.beneficiaryName || '',
                        income: salaryProfile.accountLast4 ? salaryProfile.accountLast4 : '',
                        employer: salaryProfile.bankName || '',
                        salaryPan: salaryProfile.panNumber || '',
                        aadhaar: aadhaar || null,
                        manualSalaryStatement: data.kycRecords?.manualSalaryStatement || null
                    });
                }
            })
            .catch(() => {})
            .finally(() => setIsLoading(false));
    }, [API_URL]);

    useEffect(() => { 
        if (window.opener && window.opener !== window) {
            window.close();
            return;
        }
        fetchProfile(); 
    }, [fetchProfile]);

    const kycSteps = [
        { label: 'Identity Verification', desc: 'PAN & Aadhaar linked check', pts: 200 },
        { label: 'Document Upload', desc: 'Upload your KYC documents', pts: 200 },
        { label: 'Photo & Biometric', desc: 'Selfie and liveness check', pts: 150 },
        { label: 'Review & Verification', desc: 'Team reviews your submission', pts: 50 },
    ];

    const isVerified = user.kycStatus === 'completed';
    const isInProgress = user.kycStatus === 'in_progress';

    if (isLoading && !user.id) return (
        <div className="pg-loading"><div className="pg-spinner" /><span>Loading verification details…</span></div>
    );

    return (
        <div className="pg-root ky-root">
            <section className="ky-hero">
                <div className="ky-hero-copy">
                    <span className="ky-hero-kicker"><i aria-hidden="true">ID</i>Identity Verification</span>
                    <h1>Complete <span>Your KYC</span></h1>
                    <p>Verify your identity, bank and income details to unlock all features and get faster loan approvals.</p>
                </div>
                <div className="ky-hero-art" aria-hidden="true">
                    {/*  */}
                </div>
                <div className="ky-hero-trust">
                    <div><span className="tone-green" aria-hidden="true">🛡️</span><p><strong>Secure &amp; Encrypted</strong><small>Your data is always protected</small></p></div>
                    <div><span className="tone-blue" aria-hidden="true">⏱️</span><p><strong>Fast Verification</strong><small>Complete in a few minutes</small></p></div>
                    <div><span className="tone-amber" aria-hidden="true">🏅</span><p><strong>RBI Compliant</strong><small>100% secure &amp; trusted</small></p></div>
                </div>
            </section>

            <div className="ky-layout">
                <div className="ky-main">
                    <div className="ky-card ky-process">
                        <div className="ky-process-head">
                            <div>
                                <h2>Verification Process</h2>
                                <p>Review the verification cards below and use Digilocker when you are ready to fetch Aadhaar details.</p>
                            </div>
                            <span className={`ky-status ${isVerified ? 'is-done' : isInProgress ? 'is-progress' : 'is-pending'}`}>
                                <i aria-hidden="true">{isVerified ? '✓' : isInProgress ? '↻' : '⏳'}</i>
                                {isVerified ? 'Completed' : isInProgress ? 'In Progress' : 'Pending'}
                            </span>
                        </div>
                        <KycVerification
                            userId={user.id}
                            summary={{
                                name: user.name,
                                kycStatus: user.kycStatus,
                                panNumber: user.panNumber,
                                bankAccountNumber: user.bankAccountNumber,
                                bankIfscCode: user.bankIfscCode,
                                income: user.income,
                                employer: user.employer,
                                loanAmount: '',
                                aadhaar: user.aadhaar || {},
                                manualSalaryStatement: user.manualSalaryStatement || null
                            }}
                            onComplete={() => { console.log('KYC done'); setTimeout(fetchProfile, 2000); }}
                        />
                    </div>

                    {isVerified && (
                        <div className="ky-card ky-verified">
                            <span className="ky-verified-icon" aria-hidden="true">🎉</span>
                            <div className="ky-verified-copy">
                                <h3>You're Verified!</h3>
                                <p>Your KYC is complete. You can now apply for loans and access all features.</p>
                            </div>
                            <div className="ky-verified-art" aria-hidden="true">
                                {/* <span className="ky-confetti c1" /><span className="ky-confetti c2" /><span className="ky-confetti c3" /><span className="ky-confetti c4" />
                                <span className="ky-verified-doc" />
                                <span className="ky-verified-check">✓</span> */}
                            </div>
                            <button className="ky-verified-btn" onClick={() => navigate('/dashboard/apply-loan')}>
                                Apply for a Loan <span aria-hidden="true">→</span>
                            </button>
                        </div>
                    )}
                </div>

                <aside className="ky-side">
                    <div className="ky-card ky-journey">
                        <h2>Journey Steps</h2>
                        <ol>
                            {kycSteps.map((step, i) => {
                                const done = isVerified || (isInProgress && i < 2);
                                const active = !isVerified && isInProgress && i === 2;
                                return (
                                    <li key={step.label} className={done ? 'is-done' : active ? 'is-active' : ''}>
                                        <span className="ky-journey-dot">{done ? '✓' : i + 1}</span>
                                        <div>
                                            <strong>{step.label}</strong>
                                            <small>{step.desc}</small>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>

                    <div className="ky-card ky-why">
                        <h3><span aria-hidden="true">💡</span> Why Complete KYC?</h3>
                        <ul>
                            {['Access all loan products','Faster loan approvals','Higher loan amounts available','Secure and RBI compliant process'].map(b => (
                                <li key={b}><i aria-hidden="true">✓</i>{b}</li>
                            ))}
                        </ul>
                    </div>
                </aside>
            </div>
        </div>
    );
}

export default KycPage;
